import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import OrgsTableItem from './OrgsTableItem';
import LoadingSpinner from '../../components/LoadingSpinner';

function OrgsTable({ organizationType, setOrganizationsSidebarOpen, setSelectedOrganization, refreshKey, onRefresh }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchOrganizations();
  }, [organizationType, refreshKey]);

  const fetchOrganizations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${Config.APIBaseURL}/customers/${organizationType}`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      setOrganizations(response.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectedItems(checked ? organizations.map(org => org.id) : []);
  };

  const handleSelect = (id) => {
    setSelectedItems(prevSelectedItems => {
      if (prevSelectedItems.includes(id)) {
        return prevSelectedItems.filter(itemId => itemId !== id);
      } else {
        return [...prevSelectedItems, id];
      }
    });
  };

  const handleEdit = (organization) => {
    setSelectedOrganization(organization);
    setOrganizationsSidebarOpen(true);
  };

  const handleDelete = async (organization) => {
    if (!window.confirm(`Delete "${organization.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${Config.APIBaseURL}/customer/delete/${organization.id}`,
        {
          headers: {
            'access_token': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      if (onRefresh) onRefresh();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting organization');
    }
  };

  // Filter organizations based on search term
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
      <header className="px-5 py-4">
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">
          {organizationType === 'business' ? 'Businesses' : 'Regulators'} <span className="pl-1 text-slate-400 dark:text-slate-500 font-medium">{organizations.length}</span>
        </h2>
        <div className="max-w-xl mt-2">
          <input
            className="form-input w-full"
            type="search"
            placeholder="Search by name or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </header>
      
      {isLoading ? (
        <div className="flex justify-center p-8">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-auto w-full">
            <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 dark:bg-opacity-50">
              <tr>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap w-px">
                  <div className="flex items-center">
                    <label className="inline-flex">
                      <span className="sr-only">Select all</span>
                      <input 
                        className="form-checkbox" 
                        type="checkbox" 
                        checked={selectedItems.length === organizations.length && organizations.length > 0} 
                        onChange={(e) => handleSelectAll(e.target.checked)} 
                      />
                    </label>
                  </div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Name</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Country</div>
                </th>
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Status</div>
                </th>
                {organizationType === 'business' && (
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Industry</div>
                  </th>
                )}
                {organizationType === 'regulator' && (
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Jurisdiction</div>
                  </th>
                )}
                <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  <div className="font-semibold text-left">Actions</div>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {filteredOrganizations.map(organization => (
                <OrgsTableItem
                  key={organization.id}
                  organization={organization}
                  type={organizationType}
                  handleSelect={handleSelect}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  isSelected={selectedItems.includes(organization.id)}
                />
              ))}
              {filteredOrganizations.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-2 first:pl-5 last:pr-5 py-8 text-center">
                    No organizations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OrgsTable;